import { computed, ref } from "vue";
import { defineStore } from "pinia";
import flatpickr from "flatpickr";
import { formatDateWithRelevantYear } from "#/shared/lib/helpers/localized-date";
import { createIndexFiltersApi } from "#ce/apps/SpAdmin/SpIndexFilters/api/index-filters";

// The separator sp-date-field shows between the two ends of a range.
const RANGE_SEPARATOR = flatpickr.l10ns.default.rangeSeparator;

export const FilterStatus = Object.freeze({ IDLE: "idle", LOADING: "loading", READY: "ready", ERROR: "error" });

function hasValue(value) {
  return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== "";
}

function formatFilterDate(date) {
  return formatDateWithRelevantYear(date, { month: "short", day: "numeric" }, "always");
}

function paramNames(filter) {
  if (filter.type === "date_range") {
    return [filter.type_param, filter.start_param, filter.end_param].filter(Boolean);
  }

  return [filter.param];
}

export const useIndexFiltersStore = defineStore("index-filters", () => {
  const api = createIndexFiltersApi();
  const tabs = ref([]);
  const values = ref({});
  const committedRanges = ref({});
  const activeTab = ref(null);
  const status = ref(FilterStatus.IDLE);
  const config = ref({});
  const initialQuery = new URLSearchParams(window.location.search);
  let definitionParams = new URLSearchParams();
  let generation = 0;
  let revision = 0;
  const panelOpen = ref(false);
  let pendingLoad = null;

  const filters = computed(() =>
    tabs.value.flatMap((tab) => [
      ...(tab.filters ?? []),
      ...(tab.sections ?? []).flatMap((section) => section.filters),
    ]),
  );
  const owned = computed(() => [...new Set(filters.value.flatMap(paramNames))]);
  const snapshot = computed(() => {
    const params = [];
    const applied = [];

    for (const filter of filters.value) {
      if (filter.type === "date_range") {
        const { start, end, type } = committedRanges.value[filter.start_param] ?? filter.value;

        if (!hasValue(start) && !hasValue(end)) {
          continue;
        }

        const selectedType = filter.types.find((item) => item.value === type);

        if (filter.type_param) {
          params.push([filter.type_param, type]);
        }

        if (hasValue(start)) {
          params.push([filter.start_param, start]);
        }

        if (hasValue(end)) {
          params.push([filter.end_param, end]);
        }

        applied.push({
          param: filter.start_param,
          label: selectedType?.label ?? filter.label,
          description: [start, end].filter(hasValue).map(formatFilterDate).join(RANGE_SEPARATOR),
        });
        continue;
      }

      const value = values.value[filter.param];

      if (!hasValue(value)) {
        continue;
      }

      let selected = [value].flat();
      const shared = filters.value.filter((item) => item.param === filter.param).length > 1;

      if (shared && !filter.items?.some((item) => selected.includes(item.value))) {
        continue;
      }

      if (shared) {
        selected = selected.filter((entry) => filter.items.some((item) => item.value === entry));
      }

      const titles = selected.map((entry) => filter.items?.find((item) => item.value === entry)?.title ?? entry);
      const multiple = shared || filter.multiple || filter.type === "labels";
      const name = multiple ? `${filter.param.replace(/\[\]$/, "")}[]` : filter.param;

      for (const entry of selected) {
        if (!params.some(([key, current]) => key === name && current === entry)) {
          params.push([name, entry]);
        }
      }

      if (filter.type === "labels") {
        selected.forEach((entry, index) =>
          applied.push({ param: filter.param, label: filter.label, description: titles[index], values: [entry] }),
        );
        continue;
      }

      if (shared || !applied.some((entry) => entry.param === filter.param)) {
        const entry = {
          param: filter.param,
          label: filter.label,
          description: filter.type === "hidden" ? "" : titles.join(", "),
        };

        if (shared) {
          entry.values = selected;
        }

        applied.push(entry);
      }
    }

    return { params, owned: owned.value, applied };
  });
  const appliedCount = computed(() => snapshot.value.applied.length);
  const activeChooser = computed(() => {
    const chooser = tabs.value.find((tab) => tab.key === activeTab.value)?.chooser;
    return chooser?.entries?.length && customElements.get("sp-filter-chooser-app") ? chooser : null;
  });
  const loading = computed(() => status.value === FilterStatus.LOADING);
  const error = computed(() => status.value === FilterStatus.ERROR);

  function configure(props) {
    config.value = { ...config.value, ...props };
    const url = new URL(config.value.filtersUrl || window.location.pathname, window.location.origin);
    definitionParams = new URLSearchParams();

    for (const [name, value] of url.searchParams) {
      const scalar = name.replace(/\[\]$/, "");

      if (!initialQuery.has(scalar) && !initialQuery.has(`${scalar}[]`)) {
        definitionParams.append(name, value);
      }
    }
  }

  function requestUrl() {
    const url = new URL(config.value.filtersUrl || window.location.pathname, window.location.origin);
    url.search = window.location.search;
    const params = url.searchParams;
    const filterNames = new Set(owned.value.map((name) => name.replace(/\[\]$/, "")));

    for (const name of new Set(definitionParams.keys())) {
      if (filterNames.has(name.replace(/\[\]$/, ""))) {
        continue;
      }

      params.delete(name);

      for (const value of definitionParams.getAll(name)) {
        params.append(name, value);
      }
    }

    params.set("api", "index_filters");

    return url.toString();
  }

  function initialize() {
    if (Number(config.value.appliedCount) > 0) {
      return ensureLoaded();
    }

    return Promise.resolve(false);
  }

  function open(isOpen) {
    panelOpen.value = isOpen;

    if (!isOpen || status.value === FilterStatus.READY) {
      return Promise.resolve(false);
    }

    return ensureLoaded();
  }

  function ensureLoaded() {
    if (status.value !== FilterStatus.ERROR && (tabs.value.length || status.value === FilterStatus.READY)) {
      return Promise.resolve(true);
    }

    if (pendingLoad) {
      return pendingLoad;
    }

    pendingLoad = load().finally(clearPendingLoad);

    return pendingLoad;
  }

  function clearPendingLoad() {
    pendingLoad = null;
  }

  async function load() {
    const request = ++generation;
    const previousRevision = revision;
    status.value = FilterStatus.LOADING;

    try {
      const data = await api.load(requestUrl());

      if (request !== generation) {
        return false;
      }

      if (!Array.isArray(data?.tabs)) {
        throw new TypeError("Invalid index filter definition");
      }

      const previous = values.value;
      tabs.value = data.tabs;
      const next = {};

      for (const filter of filters.value) {
        if (filter.type === "date_range") {
          if (filter.type_param) {
            next[filter.type_param] = filter.value.type;
          }

          next[filter.start_param] = filter.value.start;
          next[filter.end_param] = filter.value.end;
        } else {
          const shared = filters.value.filter((item) => item.param === filter.param).length > 1;
          if (shared) {
            next[filter.param] = [
              ...new Set([...(next[filter.param] ?? []), ...[filter.value].flat().filter(hasValue)]),
            ];
          } else {
            next[filter.param] = filter.value;
          }
        }
      }

      if (previousRevision !== revision) {
        for (const name of owned.value) {
          if (Object.hasOwn(previous, name)) {
            next[name] = previous[name];
          }
        }
      }

      values.value = next;
      const committed = {};

      for (const filter of filters.value.filter((item) => item.type === "date_range")) {
        const previousRange = committedRanges.value[filter.start_param];
        committed[filter.start_param] =
          previousRevision !== revision && previousRange ? previousRange : { ...filter.value };
      }

      committedRanges.value = committed;

      if (!tabs.value.some((tab) => tab.key === activeTab.value)) {
        activeTab.value = tabs.value[0]?.key ?? null;
      }

      status.value = FilterStatus.READY;

      return true;
    } catch {
      if (request === generation) {
        status.value = FilterStatus.ERROR;
      }

      return false;
    }
  }

  function dateRangeActive(filter) {
    return hasValue(values.value[filter.start_param]) || hasValue(values.value[filter.end_param]);
  }

  function setDateType(filter, value) {
    const before = committedRanges.value[filter.start_param] ?? filter.value;
    const changed = setValue(filter.type_param, value);
    const after = committedRanges.value[filter.start_param] ?? filter.value;
    const active = hasValue(before.start) || hasValue(before.end) || hasValue(after.start) || hasValue(after.end);
    const committedChanged = before.type !== after.type || before.start !== after.start || before.end !== after.end;

    return changed && active && committedChanged;
  }

  function dateRangeCommittable(filter) {
    return hasValue(values.value[filter.start_param]) === hasValue(values.value[filter.end_param]);
  }

  function selectedFor(filter) {
    const current = values.value[filter.param];
    const shared = filters.value.filter((item) => item.param === filter.param).length > 1;
    if (!shared) {
      return current ?? (filter.multiple ? [] : null);
    }
    const selected = [current].flat().filter((entry) => filter.items.some((item) => item.value === entry));
    return filter.multiple ? selected : (selected[0] ?? null);
  }

  function toggleLabel(filter, value, checked) {
    const selected = new Set(values.value[filter.param] ?? []);
    if (checked) {
      selected.add(value);
    } else {
      selected.delete(value);
    }
    setValue(filter.param, [...selected]);
  }

  function dateBounds(filter) {
    const type = values.value[filter.type_param] ?? filter.value.type;
    const days = filter.types.find((item) => item.value === type)?.max_lookahead_days;
    let maxDate;

    if (days !== null && days !== undefined) {
      const date = new Date();
      date.setDate(date.getDate() + Number(days));
      maxDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    }

    return { maxDate };
  }

  // Writes both ends of a range in one step. Returns whether either end changed.
  function setDateRange(filter, start, end) {
    const changed =
      (values.value[filter.start_param] ?? null) !== start || (values.value[filter.end_param] ?? null) !== end;

    if (!changed || !owned.value.includes(filter.start_param)) {
      return false;
    }

    values.value = { ...values.value, [filter.start_param]: start };
    setValue(filter.end_param, end);

    return true;
  }

  function setValue(param, value) {
    if (!owned.value.includes(param)) {
      return false;
    }

    revision++;
    values.value = { ...values.value, [param]: value };

    const editedRange = filters.value.find(
      (filter) => filter.type === "date_range" && paramNames(filter).includes(param),
    );

    if (editedRange && dateRangeCommittable(editedRange)) {
      committedRanges.value = {
        ...committedRanges.value,
        [editedRange.start_param]: {
          type: values.value[editedRange.type_param] ?? editedRange.value.type,
          start: values.value[editedRange.start_param],
          end: values.value[editedRange.end_param],
        },
      };
    }

    return true;
  }

  function setFilterValue(filter, value) {
    const shared = filters.value.filter((item) => item.param === filter.param).length > 1;
    if (!shared) {
      return setValue(filter.param, value);
    }

    const otherGroups = [values.value[filter.param]]
      .flat()
      .filter((entry) => hasValue(entry) && !filter.items.some((item) => item.value === entry));
    return setValue(filter.param, [...otherGroups, ...[value].flat().filter(hasValue)]);
  }

  function clear(param, selected) {
    const filter = filters.value.find((item) => paramNames(item).includes(param));

    if (!filter) {
      return;
    }

    if (selected) {
      setValue(
        param,
        [values.value[param]].flat().filter((entry) => !selected.includes(entry)),
      );
    } else if (filter.type === "date_range") {
      setValue(filter.start_param, null);
      setValue(filter.end_param, null);
    } else {
      setValue(param, Array.isArray(values.value[param]) ? [] : null);
    }
  }

  function clearAll() {
    for (const filter of filters.value) {
      clear(filter.type === "date_range" ? filter.start_param : filter.param);
    }
  }

  return {
    config,
    tabs,
    values,
    committedRanges,
    activeTab,
    panelOpen,
    status,
    loading,
    error,
    filters,
    snapshot,
    appliedCount,
    activeChooser,
    selectedFor,
    toggleLabel,
    dateRangeActive,
    setDateType,
    dateRangeCommittable,
    configure,
    initialize,
    open,
    ensureLoaded,
    dateBounds,
    setDateRange,
    load,
    setValue,
    setFilterValue,
    clear,
    clearAll,
  };
});
